/**
 * ItemContextMenu — the right-click menu on a layout item (2026-08-09).
 *
 * WHAT CHANGED AND WHY. Right-click on an item used to call `selectItem()`
 * directly, which slid the InspectorPanel up. Operator: *"right click on
 * items shouldn't auto bring up inspector"* — and they are right, because
 * that overloads one gesture with the app's heaviest surface. A right-click
 * is "what can I do with this thing?", not "open the editor". So the gesture
 * now opens this menu, and Inspect is one row inside it.
 *
 * ROWS. Inspect / Move · Cut / Copy / Paste / Duplicate · Delete. Paste is
 * rendered only when something has been cut or copied — a permanently-greyed
 * row is noise on a menu this short.
 *
 * MOVE is the row that earns the menu its keep. Dragging an item previously
 * required either edit mode or the DEC-054 side effect where right-click
 * selected an item so it became draggable. With right-click no longer
 * selecting, Move is the explicit replacement: it arms this one item for a
 * drag (`armItemMove`) in whatever mode you are already in and suppresses its
 * trigger while armed. The arm is STICKY - it survives the drop and lasts
 * until a press lands off the item (2026-08-09), because placing a control
 * takes several nudges and re-opening this menu between each one is not a
 * workflow. See the move-arm block in `layout/store.ts` for every exit.
 *
 * POSITIONING. Body-portalled and `position: fixed` at the cursor — the same
 * rect-anchored-popover exemption to DEC-096 that the canvas right-click
 * menu and ShapePicker take, because a menu anchored to a measured viewport
 * point cannot also live inside a transformed frame. It clamps itself into
 * the viewport after mount, flipping left/up near an edge, so a right-click
 * in the bottom-right corner does not open a menu half off-screen.
 */

import { createSignal, createEffect, onMount, onCleanup, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import type { ItemMenuState } from '../layout/itemMenu.ts';
import { closeItemMenu } from '../layout/itemMenu.ts';
import {
  layoutItems, selectItem, armItemMove,
  copyItems, cutItems, pasteItems, duplicateItems, removeItems,
  hasItemClipboard, selectedItemIds, isItemSelected, announceAdded,
} from '../layout/store.ts';
import { hueOf, HUE_VALUES, type PaletteHue } from '../layout/projectStore.ts';
import { kindLabel } from '../layout/kindLabels.ts';
import { activeSkin } from '../settings/store.ts';

/** Gap kept between the menu and the viewport edge when it has to clamp. */
const EDGE_PAD = 8;

/* Row glyphs. Stroke-only, `currentColor`, so each row's icon is exactly its
   text colour — including the fault-red Delete — and no per-skin rule is
   needed. DEC-133 rule 3: currentColor resolves to a FLAT token here
   (--sk-ink / --sk-ink-dim / --sk-fault), never a gradient. */
function Glyph(props: { d: string }) {
  return (
    <svg class="item-ctx-glyph" viewBox="0 0 16 16" aria-hidden="true">
      <path d={props.d} fill="none" stroke="currentColor" stroke-width="1.4"
        stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

const ICON = {
  inspect: 'M2.5 8s2.2-3.5 5.5-3.5S13.5 8 13.5 8s-2.2 3.5-5.5 3.5S2.5 8 2.5 8Z M8 6.4a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2Z',
  move: 'M8 2v12 M2 8h12 M8 2 6 4 M8 2l2 2 M8 14l-2-2 M8 14l2-2 M2 8l2-2 M2 8l2 2 M14 8l-2-2 M14 8l-2 2',
  cut: 'M4 2.5 11.5 12 M12 2.5 4.5 12 M4.5 13.5a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2Z M11.5 13.5a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2Z',
  copy: 'M5.5 5.5h7v8h-7z M3.5 10.5v-8h7',
  paste: 'M5.5 3H4a.9.9 0 0 0-.9.9v9.2A.9.9 0 0 0 4 14h8a.9.9 0 0 0 .9-.9V3.9A.9.9 0 0 0 12 3h-1.5 M6 2h4v2.2H6z',
  duplicate: 'M6.5 6.5h6v6h-6z M3.5 9.5v-6h6 M9.5 8v3 M8 9.5h3',
  delete: 'M3 4.5h10 M6.5 4.5V3h3v1.5 M4.5 4.5l.6 8.1a.9.9 0 0 0 .9.9h4a.9.9 0 0 0 .9-.9l.6-8.1 M7 7v4 M9 7v4',
} as const;

interface RowProps {
  label: string;
  icon: string;
  tone?: 'destructive';
  onPick: () => void;
}

function Row(props: RowProps) {
  return (
    <button
      type="button"
      class="item-ctx-row"
      data-row={props.label.toLowerCase()}
      data-tone={props.tone ?? 'normal'}
      /* onPointerDown, not onClick: the backdrop sits under the menu and
         also listens, and a click that lands as the menu is being torn down
         would otherwise be swallowed. Pointer-down is also what makes the
         menu feel instant rather than mushy. */
      onPointerDown={(ev) => { ev.preventDefault(); ev.stopPropagation(); props.onPick(); }}
    >
      <Glyph d={props.icon} />
      <span class="item-ctx-label">{props.label}</span>
    </button>
  );
}

export function ItemContextMenu(props: { state: ItemMenuState }) {
  let menuRef: HTMLDivElement | undefined;
  // Rendered at the raw cursor point first, then corrected once measured.
  // Starting hidden would cost a frame of flicker; starting at the cursor
  // means the common (nowhere near an edge) case never moves at all.
  const [pos, setPos] = createSignal({ x: props.state.x, y: props.state.y });

  const item = () => layoutItems().find((it) => it.id === props.state.itemId) ?? null;
  const itemName = () => (item() as { name?: string } | null)?.name?.trim() ?? '';
  const itemHue = () => hueOf((item() ?? {}) as { hue?: PaletteHue; color?: string });

  /**
   * WHAT THE ROWS ACT ON (2026-08-09, multi-select). Operator: right-click any
   * highlighted item and *"use the right click menu"* on all of them.
   *
   * The right-clicked item is guaranteed to be IN the selection by the time
   * this renders - EditableItem collapses the selection onto an unselected
   * item before opening the menu, the Finder/Explorer convention - so this is
   * simply "the selection", never a union of two ideas. That is the whole
   * reason the rows can stay one-liners.
   */
  const targets = () => (isItemSelected(props.state.itemId)
    ? selectedItemIds()
    : [props.state.itemId]);
  const count = () => targets().length;
  const multi = () => count() > 1;

  function clampIntoViewport() {
    if (!menuRef) return;
    const { width, height } = menuRef.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Flip rather than merely clamp: a menu that has to shift left anyway
    // reads as anchored to the cursor's other side, which is the desktop
    // convention and keeps the cursor outside the rows (no accidental hover).
    let x = props.state.x;
    let y = props.state.y;
    if (x + width > vw - EDGE_PAD) x = Math.max(EDGE_PAD, props.state.x - width);
    if (y + height > vh - EDGE_PAD) y = Math.max(EDGE_PAD, props.state.y - height);
    setPos({ x, y });
  }
  onMount(clampIntoViewport);
  // Re-clamp if the same mounted menu is re-pointed at another spot (Show is
  // non-keyed, so opening on a second item can reuse this instance).
  createEffect(() => { props.state.x; props.state.y; clampIntoViewport(); });

  function onKey(ev: KeyboardEvent) {
    if (ev.key === 'Escape') { ev.stopPropagation(); closeItemMenu(); }
  }
  onMount(() => window.addEventListener('keydown', onKey, true));
  onCleanup(() => window.removeEventListener('keydown', onKey, true));

  const pick = (fn: () => void) => { fn(); closeItemMenu(); };

  return (
    <Portal>
      <div
        class="item-ctx-backdrop"
        onPointerDown={closeItemMenu}
        onContextMenu={(ev) => { ev.preventDefault(); closeItemMenu(); }}
      />
      <div
        ref={menuRef}
        /* `skin` + data-skin on the element itself: this is body-portalled,
           outside `.shell` where data-skin normally lives. The root carries it
           too, but stating it here is what lets the menu be screenshot or
           previewed in isolation without silently falling back to Default. */
        class="item-ctx-menu skin"
        data-skin={activeSkin()}
        role="menu"
        aria-label={`${kindLabel(item()?.kind ?? 'item')} actions`}
        style={{
          left: `${pos().x}px`,
          top: `${pos().y}px`,
          '--hue': HUE_VALUES[itemHue()],
          '--hue-ink': itemHue() === 'none' ? 'var(--sk-ink)' : HUE_VALUES[itemHue()],
        }}
      >
        {/* Header: says WHAT you right-clicked. On a dense page the menu can
            open over a neighbour, and a menu that does not name its target is
            how you delete the wrong control. */}
        <div class="item-ctx-head" data-multi={multi() ? 'true' : undefined}>
          <Show
            when={multi()}
            fallback={
              <>
                <span class="item-ctx-kind">{kindLabel(item()?.kind ?? 'item')}</span>
                <Show when={itemName()}>
                  <span class="item-ctx-name">{itemName()}</span>
                </Show>
              </>
            }
          >
            {/* The COUNT is the header for a group, and it is not decoration:
                Delete is one row down and irreversible-feeling, so the number
                of things it is about has to be the first thing read. */}
            <span class="item-ctx-kind">{count()} items selected</span>
            <span class="item-ctx-name">every action below applies to all {count()}</span>
          </Show>
        </div>

        {/* Inspect is the ONE row that stays single, by explicit ask: *"inspector
            will only open the one that was last right clicked on"*. It collapses
            the selection onto this item, which is also what re-opens the panel -
            the inspector is deliberately hidden while several are highlighted,
            since its fields edit exactly one item. */}
        <Row
          label="Inspect"
          icon={ICON.inspect}
          onPick={() => pick(() => selectItem(props.state.itemId))}
        />
        <Row label="Move" icon={ICON.move} onPick={() => pick(() => armItemMove(props.state.itemId))} />

        <div class="item-ctx-sep" />

        <Row label="Cut" icon={ICON.cut} onPick={() => pick(() => cutItems(targets()))} />
        <Row label="Copy" icon={ICON.copy} onPick={() => pick(() => copyItems(targets()))} />
        <Show when={hasItemClipboard()}>
          <Row
            label="Paste"
            icon={ICON.paste}
            /* Pastes where the menu was opened, not next to the source: the
               operator right-clicked a specific spot and that is the answer to
               "where do you want it". Falls back to cascading off the source
               when the point could not be resolved to canvas space. */
            onPick={() => pick(() => announceAdded(pasteItems(props.state.canvas ?? undefined).map((it) => it.id)))}
          />
        </Show>
        <Row label="Duplicate" icon={ICON.duplicate} onPick={() => pick(() => announceAdded(duplicateItems(targets()).map((it) => it.id)))} />

        <div class="item-ctx-sep" />

        <Row
          label="Delete"
          icon={ICON.delete}
          tone="destructive"
          onPick={() => pick(() => removeItems(targets()))}
        />
      </div>
    </Portal>
  );
}
